import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { UserSetting } from 'src/app/user/model/user-setting';
import { SessionSetting } from '../model/session-setting';
import { Cacheable, CacheBuster } from 'ts-cacheable';
import { SettingList } from 'src/app/setting/model/settingList';
import { SettingEdit } from 'src/app/setting/model/settingEdit';
import { MessengerService } from '../service/messenger.service';
import { TranslateService } from '@ngx-translate/core';

const settingCacheBuster = new Subject<void>();

@Injectable({
  providedIn: 'root'
})
export class SettingService implements OnDestroy
{
  public configurableDashboard: Array<{ text: string; value: string }> = [
    { text: 'Caseload', value: 'CASELOAD' },
    { text: 'Events', value: 'EVENTS' },
    { text: 'Event Stream', value: 'EVENT_STREAM' }
  ];

  public communityDefaultLandingPage: Array<{ text: string; value: string }> = [
    { text: 'Client Search', value: 'CLIENT_SEARCH' },
    { text: 'Case List', value: 'CASE_LIST' }
  ];

  private settingUpdatedSubscription: Subscription;

  private messagingLanguageChangedSubscription: Subscription;

  constructor(
    private http: HttpClient,
    private messengerService: MessengerService,
    private translate: TranslateService,)
  {
    this.settingUpdatedSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message && message.type && message.type === 'wsSettingsUpdated')
        {
          settingCacheBuster.next();
        }
      }
    });

    this.loadLocalizations();
    this.messagingLanguageChangedSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message && message.type && message.type === 'languageChanged')
        {
          this.loadLocalizations();
        }
      }
    });
  }

  /**
   * Get user settings
   *
   * @returns Observable<UserSetting> HTTP observable response.
   */
  @Cacheable(
    {
      cacheBusterObserver: settingCacheBuster
    })
  public getCachedUserSettings(): Observable<UserSetting>
  {
    return this.http.get<UserSetting>('/unauthenticated/settings/user');
  }

  /**
   * Get session settings
   *
   * @returns Observable<SessionSetting> HTTP observable response.
   */
  @Cacheable(
    {
      cacheBusterObserver: settingCacheBuster
    })
  public getCachedSessionSettings(): Observable<SessionSetting>
  {
    return this.http.get<SessionSetting>('/unauthenticated/settings/session');
  }

  /**
   * Save a Setting.
   *
   * @param data Setting to persit
   */
  @CacheBuster(
    {
      cacheBusterNotifier: settingCacheBuster
    })
  public saveSetting(data: any, settingId: number): Observable<any>
  {
    return this.http.put(`/setting/${settingId}`, data);
  }

  public ngOnDestroy(): void
  {
    if (this.settingUpdatedSubscription !== undefined) { this.settingUpdatedSubscription.unsubscribe(); }
    if (this.messagingLanguageChangedSubscription !== undefined) { this.messagingLanguageChangedSubscription.unsubscribe(); }
  }

  /**
   * Get Settings for grid list.
   */
  public getSettingsList(): Observable<SettingList[]>
  {
    return this.http.get<SettingList[]>('/settings/list');
  }

  /**
   * Get Setting by id.
   *
   * @param id Setting id
   */
  public getSettingById(id: number): Observable<SettingEdit>
  {
    return this.http.get<SettingEdit>(`/setting/${id}`);
  }

  /**
   * Get Setting by key.
   *
   * @param id Setting key
   * @param organizationId OrganizationId
   */
  public getSettingByKey(key: string, organizationId = ''): Observable<SettingEdit>
  {
    const orgParam = (organizationId !== '') ? `?organizationId=${organizationId}` : '';
    return this.http.get<SettingEdit>(`/unauthenticated/settings/${key}${orgParam}`);
  }

  /**
   * This function is checking if the setting key is valid for the role. The Referral Coordinator role is limited
   * to some setting and not relevant in order context.
   *
   * @param key Setting key
   * @param role User role
   * @returns Returns true if the role is valid for the setting key.
   */
  public isValidSettingForRole(key: string, role: string): boolean
  {
    if (key === 'AllowOrganizationGroupsRoles') 
    {
      return role === 'ROLE_PROGRAM_LEAD' || role === 'ROLE_COMMUNITY_COORDINATOR' || role === 'ROLE_OPERATOR';
    }
    else if (key === 'AllowOrganizationUsersRoles')
    {
      return role === 'ROLE_PROGRAM_LEAD' || role === 'ROLE_OPERATOR';
    }
    else if (key === 'AddUpdateChannelCategoriesRoles' || key === 'AddUpdateDictionariesRoles' ||
      key === 'AddUpdateExtensionsRoles' || key === 'AddUpdateFieldConfigurationsRoles' ||
      key === 'AddUpdateMetricsRoles' || key === 'AddUpdateProgramCategoriesRoles' ||
      key === 'AddUpdateServiceCategoriesRoles' || key === 'AddUpdateTermsAndConditionsRoles' ||
      key === 'AddUpdateAssessmentsRoles' || key === 'AddUpdateAssessmentTypesRoles' ||
      key === 'AddUpdateProgramTemplatesRoles' || key === 'AddUpdateFacilitiesRoles' ||
      key === 'AddUpdateClientImportRoles' || key === 'AddUpdateProvidersRoles' || key === 'AddUpdateTeamRolesRoles')
    {
      return role === 'ROLE_PROGRAM_LEAD' || role === 'ROLE_OPERATOR';
    }

    switch (role)
    {
      case 'ROLE_REFERRAL_COORDINATOR':
        return key === 'AttachmentsRoles' || key === 'ReportRoles' || key === 'CommunityServiceSearchRoles' ||
          key === 'MessagesRoles' || key === 'ProvidersRoles' || key === 'AnalyticsRoles' ||
          key === 'ChatRoles';
      case 'ROLE_DIRECTORY_MANAGER':
        return key === 'MessagesRoles' || key === 'CommunityServiceSearchRoles' || key === 'ProvidersRoles' || key === 'AnalyticsRoles' ||
          key === 'ChatRoles' || key === 'AddUpdateCommunityResourcesRoles' || key === 'AttachmentsRoles';
      case 'ROLE_DATA_ANALYST':
        return key === 'MessagesRoles' || key === 'ReportRoles' || key === 'AnalyticsRoles' ||
          key === 'ChatRoles' || key === 'AttachmentsRoles';
      case 'ROLE_CARE_COORDINATOR':
      case 'ROLE_COMMUNITY_COORDINATOR':
        return key !== 'AddUpdateCommunityResourcesRoles';
      case 'ROLE_REVENUE_MANAGER_OPERATOR':
      case 'ROLE_REVENUE_MANAGER_ANALYST':
      case 'ROLE_CBO_REVENUE_ANALYST':
        return key === 'MessagesRoles' || key === 'AttachmentsRoles';
      default:
        return true;
    }
  }

  public getCommunityDefaultLandingPagedText(item: string): string
  {
    let text = item;
    const communityObject = this.communityDefaultLandingPage.find(x => x.value === item);
    if (communityObject !== undefined)
    {
      text = communityObject.text;
    }
    return text;
  }

  public convertCommunityDefaultLandingPageToDisplayTexts(communityDefaultLandingPage: string): string
  {
    const res = communityDefaultLandingPage.split(',');
    const communityDefaultLandingPageText = [];
    res.forEach(item =>
    {
      const itemText = this.getCommunityDefaultLandingPagedText(item);
      if (itemText !== '')
      {
        communityDefaultLandingPageText.push(itemText);
      }
    });
    return communityDefaultLandingPageText.join();
  }

  public convertPayerBillingIdentifierToDisplayTexts(allCommunityServiceIdentifierTypes: any[], payerBillingIdentifierId: string): string
  {
    let payerBillingIdentifierText = '';    
    const payerBillingIdentifier = allCommunityServiceIdentifierTypes.find(x => x.id === payerBillingIdentifierId);
    if (payerBillingIdentifier !== undefined)
    {
      payerBillingIdentifierText = payerBillingIdentifier.displayName;
    }        
    return payerBillingIdentifierText;
  }

  public getConfigurableDashboardText(item: string): string
  {
    let text = item;
    const dashboardObject = this.configurableDashboard.find(x => x.value === item);
    if (dashboardObject !== undefined)
    {
      text = dashboardObject.text;
    }
    return text;
  }

  public convertConfigurableDashboardToDisplayTexts(configurableDashboard: string): string
  {
    const res = configurableDashboard.split(',');
    const configurableDashboardText = [];
    res.forEach(item =>
    {
      const itemText = this.getConfigurableDashboardText(item);
      if (itemText !== '')
      {
        configurableDashboardText.push(itemText);
      }
    });
    return configurableDashboardText.join();
  }

  /**
   * Load localizations
   */
  private loadLocalizations(): void
  {
    setTimeout(() =>
    {
      this.translate.get(['CONFIGURABLE-DASHBOARD-ITEMS']).subscribe((res: string) =>
      {
        const splitted = res['CONFIGURABLE-DASHBOARD-ITEMS'].split(',');
        for (let i = 0; i < splitted.length; i++)
        {
          if (this.configurableDashboard.length > i)
          {
            this.configurableDashboard[i].text = splitted[i];
          }
        }
      });

      this.getCachedSessionSettings().subscribe({
        next: (sessionSetting) =>
        {
          this.translate.get(['COMMUNITY-DEFAULT-LANDING-PAGES']).subscribe((res: string) =>
          {
            const splitted = res['COMMUNITY-DEFAULT-LANDING-PAGES'].split(',');
            for (let i = 0; i < splitted.length; i++)
            {
              if (this.communityDefaultLandingPage.length > i)
              {
                this.communityDefaultLandingPage[i].text = splitted[i].replace('{{patientLabel}}', sessionSetting.patientLabel);
              }
            }
          })
        }
      });
    });
  }
}
